// Programmatic SEO landing page registry — /collections/:slug.
//
// Each definition maps to a filtered slice of live auctions using the same
// AuctionCardData fields the /auctions browse page filters on. Keep slugs
// stable forever (they're indexed) and only add, never remove/rename.

import type { AuctionCardData } from "@/components/auction-card";

export type LandingKind = "game" | "grade" | "character" | "language" | "format";

export type LandingFaq = { qKey: string; qDefault: string; aKey: string; aDefault: string };

export type LandingFilters = {
  /** Lowercase substrings matched against the auction title (OR). */
  titleKeywords?: string[];
  gradingCompany?: string[]; // matches grading_company (case-insensitive)
  grade?: string[]; // matches grade / custom_grade
  cardLanguage?: string[]; // matches card_language
  productType?: string[]; // matches product_type
};

export type LandingDefinition = {
  slug: string;
  kind: LandingKind;
  /** Nav/label use. */
  label: string;
  h1: string;
  /** i18n key suffix within landing.json (title/description/intro under this slug). */
  i18nKey: string;
  /** Equivalent /auctions search params for the "see all in browse" link. */
  browseSearch: Record<string, string>;
  filters: LandingFilters;
  faqs: LandingFaq[];
  relatedSlugs: string[];
};

function kw(...words: string[]): string[] {
  return words.map((w) => w.toLowerCase());
}

export const LANDING_DEFINITIONS: LandingDefinition[] = [
  {
    slug: "pokemon-auctions",
    kind: "game",
    label: "Pokémon Auctions",
    h1: "Pokémon Card Auctions",
    i18nKey: "pokemonAuctions",
    browseSearch: { q: "pokemon" },
    filters: { titleKeywords: kw("pokemon", "pokémon") },
    faqs: [
      { qKey: "pokemon.q1", qDefault: "Are these Pokémon cards authentic?", aKey: "pokemon.a1", aDefault: "Every listing is sold by a verified UK seller and graded cards include certificate details from PSA, BGS or CGC where applicable." },
      { qKey: "pokemon.q2", qDefault: "Can I buy raw (ungraded) Pokémon cards?", aKey: "pokemon.a2", aDefault: "Yes — both raw and professionally graded Pokémon cards are listed, filterable by grading company and grade." },
      { qKey: "pokemon.q3", qDefault: "What payment methods are accepted?", aKey: "pokemon.a3", aDefault: "All auctions are priced in GBP and paid securely via Stripe, with funds held until you confirm delivery." },
      { qKey: "pokemon.q4", qDefault: "Do you sell sealed Pokémon product?", aKey: "pokemon.a4", aDefault: "Yes, sealed booster boxes, packs and tins appear alongside singles — use the product type filter on the browse page." },
    ],
    relatedSlugs: ["charizard", "pikachu", "umbreon", "psa-10-cards", "japanese-cards"],
  },
  {
    slug: "one-piece-auctions",
    kind: "game",
    label: "One Piece Auctions",
    h1: "One Piece Card Game Auctions",
    i18nKey: "onePieceAuctions",
    browseSearch: { q: "one piece" },
    filters: { titleKeywords: kw("one piece") },
    faqs: [
      { qKey: "onePiece.q1", qDefault: "What is the One Piece Card Game?", aKey: "onePiece.a1", aDefault: "A trading card game by Bandai based on the One Piece manga and anime, popular with collectors worldwide." },
      { qKey: "onePiece.q2", qDefault: "Are Japanese and English One Piece cards both available?", aKey: "onePiece.a2", aDefault: "Yes — filter by card language to find English or Japanese print One Piece cards." },
      { qKey: "onePiece.q3", qDefault: "Can I find Luffy cards specifically?", aKey: "onePiece.a3", aDefault: "Yes, see our dedicated Luffy collection for character-specific listings." },
      { qKey: "onePiece.q4", qDefault: "Are graded One Piece cards available?", aKey: "onePiece.a4", aDefault: "Yes, PSA, BGS and CGC graded One Piece cards are listed alongside raw copies." },
    ],
    relatedSlugs: ["luffy", "manga-cards", "japanese-cards", "psa-10-cards"],
  },
  {
    slug: "yu-gi-oh-auctions",
    kind: "game",
    label: "Yu-Gi-Oh! Auctions",
    h1: "Yu-Gi-Oh! Card Auctions",
    i18nKey: "yugiohAuctions",
    browseSearch: { q: "yu-gi-oh" },
    filters: { titleKeywords: kw("yu-gi-oh", "yugioh", "yu gi oh") },
    faqs: [
      { qKey: "yugioh.q1", qDefault: "Do you have vintage Yu-Gi-Oh! cards?", aKey: "yugioh.a1", aDefault: "Yes, vintage and modern Yu-Gi-Oh! singles are listed, including 1st Edition and Limited Edition cards." },
      { qKey: "yugioh.q2", qDefault: "Are Yu-Gi-Oh! cards graded?", aKey: "yugioh.a2", aDefault: "Many listings are professionally graded by PSA, BGS or CGC; raw cards are also available." },
      { qKey: "yugioh.q3", qDefault: "Can I buy Japanese Yu-Gi-Oh! cards?", aKey: "yugioh.a3", aDefault: "Yes, filter by card language to find Japanese print Yu-Gi-Oh! cards." },
      { qKey: "yugioh.q4", qDefault: "Is buy-it-now available?", aKey: "yugioh.a4", aDefault: "Many sellers offer a Buy Now price alongside live bidding — look for the Buy Now badge on listings." },
    ],
    relatedSlugs: ["japanese-cards", "psa-10-cards", "english-cards"],
  },
  {
    slug: "psa-10-cards",
    kind: "grade",
    label: "PSA 10 Cards",
    h1: "PSA 10 Gem Mint Card Auctions",
    i18nKey: "psa10Cards",
    browseSearch: { grader: "PSA", grade: "10" },
    filters: { gradingCompany: ["psa"], grade: ["10"] },
    faqs: [
      { qKey: "psa10.q1", qDefault: "What does PSA 10 mean?", aKey: "psa10.a1", aDefault: "PSA 10 (Gem Mint) is the highest grade awarded by Professional Sports Authenticator, indicating a virtually flawless card." },
      { qKey: "psa10.q2", qDefault: "Can I verify the PSA certificate?", aKey: "psa10.a2", aDefault: "Yes, PSA-graded listings include the certificate number for independent verification on PSA's website." },
      { qKey: "psa10.q3", qDefault: "Which games have PSA 10 listings?", aKey: "psa10.a3", aDefault: "PSA 10 cards span Pokémon, One Piece, Yu-Gi-Oh!, sports and more — use the browse filters to narrow by game." },
      { qKey: "psa10.q4", qDefault: "Are PSA 10 cards more expensive?", aKey: "psa10.a4", aDefault: "Gem Mint condition typically commands a premium over lower grades due to scarcity and eye appeal." },
    ],
    relatedSlugs: ["bgs-black-label", "cgc-pristine", "pokemon-auctions"],
  },
  {
    slug: "bgs-black-label",
    kind: "grade",
    label: "BGS Black Label",
    h1: "BGS Black Label 10 Card Auctions",
    i18nKey: "bgsBlackLabel",
    browseSearch: { grader: "BGS", grade: "10" },
    filters: { gradingCompany: ["bgs"], grade: ["10", "black label", "10 black label"] },
    faqs: [
      { qKey: "bgsBlack.q1", qDefault: "What is a BGS Black Label?", aKey: "bgsBlack.a1", aDefault: "A Black Label is Beckett's rarest designation — a perfect 10 across all four subgrades (centering, corners, edges, surface)." },
      { qKey: "bgsBlack.q2", qDefault: "How rare are Black Label cards?", aKey: "bgsBlack.a2", aDefault: "Extremely rare — most graded populations show only a handful of Black Label examples per set." },
      { qKey: "bgsBlack.q3", qDefault: "Can I verify a BGS certificate?", aKey: "bgsBlack.a3", aDefault: "Yes, BGS-graded listings include the cert number so you can verify subgrades on Beckett's site." },
      { qKey: "bgsBlack.q4", qDefault: "Do Black Label cards sell for a premium?", aKey: "bgsBlack.a4", aDefault: "Yes, due to their rarity Black Label cards typically command significant premiums over standard BGS 10s." },
    ],
    relatedSlugs: ["psa-10-cards", "cgc-pristine"],
  },
  {
    slug: "cgc-pristine",
    kind: "grade",
    label: "CGC Pristine 10",
    h1: "CGC Pristine 10 Card Auctions",
    i18nKey: "cgcPristine",
    browseSearch: { grader: "CGC", grade: "Pristine 10" },
    filters: { gradingCompany: ["cgc"], grade: ["pristine 10", "10", "pristine"] },
    faqs: [
      { qKey: "cgcPristine.q1", qDefault: "What is CGC Pristine 10?", aKey: "cgcPristine.a1", aDefault: "Pristine 10 is CGC's top grade, reserved for cards with essentially flawless centering, surfaces, corners and edges." },
      { qKey: "cgcPristine.q2", qDefault: "Is CGC Pristine 10 the same as PSA 10?", aKey: "cgcPristine.a2", aDefault: "They use different scales — CGC Pristine 10 is generally considered comparable to or stricter than a PSA 10." },
      { qKey: "cgcPristine.q3", qDefault: "Which games are commonly CGC graded?", aKey: "cgcPristine.a3", aDefault: "CGC grades trading cards, manga, comics and sealed wax across Pokémon, One Piece, sports and more." },
      { qKey: "cgcPristine.q4", qDefault: "Can I verify a CGC certificate?", aKey: "cgcPristine.a4", aDefault: "Yes, listings include the CGC certificate number for lookup on CGC's website." },
    ],
    relatedSlugs: ["psa-10-cards", "bgs-black-label"],
  },
  {
    slug: "charizard",
    kind: "character",
    label: "Charizard",
    h1: "Charizard Card Auctions",
    i18nKey: "charizard",
    browseSearch: { q: "charizard" },
    filters: { titleKeywords: kw("charizard") },
    faqs: [
      { qKey: "charizard.q1", qDefault: "Why is Charizard so popular with collectors?", aKey: "charizard.a1", aDefault: "Charizard has been a fan-favourite Pokémon since the original Base Set, driving strong long-term collector demand." },
      { qKey: "charizard.q2", qDefault: "Are graded Charizard cards available?", aKey: "charizard.a2", aDefault: "Yes, PSA, BGS and CGC graded Charizard cards are listed alongside raw copies across many sets." },
      { qKey: "charizard.q3", qDefault: "Can I find Japanese Charizard cards?", aKey: "charizard.a3", aDefault: "Yes, filter by card language to find Japanese print Charizard cards." },
      { qKey: "charizard.q4", qDefault: "Do you have vintage Base Set Charizard cards?", aKey: "charizard.a4", aDefault: "Vintage and modern Charizard cards both appear — check the listing details for set and edition." },
    ],
    relatedSlugs: ["pikachu", "umbreon", "pokemon-auctions", "psa-10-cards"],
  },
  {
    slug: "pikachu",
    kind: "character",
    label: "Pikachu",
    h1: "Pikachu Card Auctions",
    i18nKey: "pikachu",
    browseSearch: { q: "pikachu" },
    filters: { titleKeywords: kw("pikachu") },
    faqs: [
      { qKey: "pikachu.q1", qDefault: "What makes Pikachu cards collectible?", aKey: "pikachu.a1", aDefault: "As Pokémon's mascot, Pikachu appears in countless promo, illustrator and special-edition prints prized by collectors." },
      { qKey: "pikachu.q2", qDefault: "Are Pikachu Illustrator-style promos listed?", aKey: "pikachu.a2", aDefault: "Rare promo Pikachu cards appear occasionally — check current listings and set your search to be notified." },
      { qKey: "pikachu.q3", qDefault: "Can I find graded Pikachu cards?", aKey: "pikachu.a3", aDefault: "Yes, PSA, BGS and CGC graded Pikachu cards are available alongside raw copies." },
      { qKey: "pikachu.q4", qDefault: "Do you have Japanese Pikachu cards?", aKey: "pikachu.a4", aDefault: "Yes, filter by card language to find Japanese print Pikachu cards." },
    ],
    relatedSlugs: ["charizard", "umbreon", "pokemon-auctions"],
  },
  {
    slug: "umbreon",
    kind: "character",
    label: "Umbreon",
    h1: "Umbreon Card Auctions",
    i18nKey: "umbreon",
    browseSearch: { q: "umbreon" },
    filters: { titleKeywords: kw("umbreon") },
    faqs: [
      { qKey: "umbreon.q1", qDefault: "Why are Umbreon cards so sought after?", aKey: "umbreon.a1", aDefault: "Umbreon's Eeveelution artwork, especially Gold Star and VMAX Alt Art prints, is highly prized by collectors." },
      { qKey: "umbreon.q2", qDefault: "Are graded Umbreon cards listed?", aKey: "umbreon.a2", aDefault: "Yes, PSA, BGS and CGC graded Umbreon cards appear alongside raw copies." },
      { qKey: "umbreon.q3", qDefault: "Can I find Umbreon VMAX Alt Art?", aKey: "umbreon.a3", aDefault: "Alt Art and secret rare Umbreon cards appear when available — check current live listings." },
      { qKey: "umbreon.q4", qDefault: "Do you have Japanese Umbreon prints?", aKey: "umbreon.a4", aDefault: "Yes, filter by card language to find Japanese print Umbreon cards." },
    ],
    relatedSlugs: ["pikachu", "charizard", "pokemon-auctions"],
  },
  {
    slug: "luffy",
    kind: "character",
    label: "Luffy",
    h1: "Monkey D. Luffy Card Auctions",
    i18nKey: "luffy",
    browseSearch: { q: "luffy" },
    filters: { titleKeywords: kw("luffy") },
    faqs: [
      { qKey: "luffy.q1", qDefault: "Which sets feature Luffy cards?", aKey: "luffy.a1", aDefault: "Luffy appears across many One Piece Card Game sets as a Leader and Character card, including sought-after alt art prints." },
      { qKey: "luffy.q2", qDefault: "Are graded Luffy cards available?", aKey: "luffy.a2", aDefault: "Yes, PSA, BGS and CGC graded Luffy cards appear alongside raw copies." },
      { qKey: "luffy.q3", qDefault: "Can I find Japanese Luffy cards?", aKey: "luffy.a3", aDefault: "Yes, filter by card language to find Japanese print Luffy cards." },
      { qKey: "luffy.q4", qDefault: "Do you have manga-style Luffy cards?", aKey: "luffy.a4", aDefault: "Manga-art parallel cards featuring Luffy occasionally appear — see our manga cards collection too." },
    ],
    relatedSlugs: ["one-piece-auctions", "manga-cards", "japanese-cards"],
  },
  {
    slug: "manga-cards",
    kind: "format",
    label: "Manga Cards",
    h1: "Manga-Art Trading Card Auctions",
    i18nKey: "mangaCards",
    browseSearch: { q: "manga" },
    filters: { titleKeywords: kw("manga") },
    faqs: [
      { qKey: "manga.q1", qDefault: "What are manga cards?", aKey: "manga.a1", aDefault: "Manga cards are parallel prints using black-and-white or manga-style artwork instead of standard illustrations, popular in One Piece and other TCGs." },
      { qKey: "manga.q2", qDefault: "Which games have manga-art cards?", aKey: "manga.a2", aDefault: "The One Piece Card Game is best known for manga rares, though other games occasionally issue similar parallels." },
      { qKey: "manga.q3", qDefault: "Are manga cards graded?", aKey: "manga.a3", aDefault: "Yes, graded and raw manga cards both appear — filter by grading company to narrow results." },
      { qKey: "manga.q4", qDefault: "Are manga cards rarer than standard prints?", aKey: "manga.a4", aDefault: "Manga rares are typically a lower-pull-rate parallel, making them more scarce than standard rarity prints." },
    ],
    relatedSlugs: ["luffy", "one-piece-auctions", "japanese-cards"],
  },
  {
    slug: "japanese-cards",
    kind: "language",
    label: "Japanese Cards",
    h1: "Japanese Print Trading Card Auctions",
    i18nKey: "japaneseCards",
    browseSearch: { card_language: "japanese" },
    filters: { cardLanguage: ["japanese", "jp"] },
    faqs: [
      { qKey: "japanese.q1", qDefault: "Why do collectors prefer Japanese print cards?", aKey: "japanese.a1", aDefault: "Japanese cards are often printed on higher-quality cardstock, released earlier, and feature unique artwork variations." },
      { qKey: "japanese.q2", qDefault: "Are Japanese cards graded the same way?", aKey: "japanese.a2", aDefault: "Yes, PSA, BGS and CGC all grade Japanese-language cards using the same scales as English cards." },
      { qKey: "japanese.q3", qDefault: "Which games have Japanese listings?", aKey: "japanese.a3", aDefault: "Pokémon, One Piece and Yu-Gi-Oh! all have active Japanese print listings — use the game filters to browse." },
      { qKey: "japanese.q4", qDefault: "Do Japanese cards cost more than English?", aKey: "japanese.a4", aDefault: "Pricing depends on rarity and condition rather than language alone, though some Japanese exclusives command premiums." },
    ],
    relatedSlugs: ["english-cards", "pokemon-auctions", "one-piece-auctions"],
  },
  {
    slug: "english-cards",
    kind: "language",
    label: "English Cards",
    h1: "English Print Trading Card Auctions",
    i18nKey: "englishCards",
    browseSearch: { card_language: "english" },
    filters: { cardLanguage: ["english", "en"] },
    faqs: [
      { qKey: "english.q1", qDefault: "Are most listings in English?", aKey: "english.a1", aDefault: "English print cards make up the majority of listings across Pokémon, One Piece, Yu-Gi-Oh! and sports cards." },
      { qKey: "english.q2", qDefault: "Are English cards graded?", aKey: "english.a2", aDefault: "Yes, PSA, BGS, CGC and other graders are represented, alongside raw ungraded English cards." },
      { qKey: "english.q3", qDefault: "Can I filter by set and edition?", aKey: "english.a3", aDefault: "Yes, use the browse page filters for card set, edition (1st Edition/Unlimited) and finish (holo/reverse holo)." },
      { qKey: "english.q4", qDefault: "Do you have English sealed product?", aKey: "english.a4", aDefault: "Yes, English booster boxes and packs are listed alongside singles." },
    ],
    relatedSlugs: ["japanese-cards", "pokemon-auctions", "yu-gi-oh-auctions"],
  },
];

export function listLandingSlugs(): string[] {
  return LANDING_DEFINITIONS.map((d) => d.slug);
}

export function findLanding(slug: string | undefined | null): LandingDefinition | null {
  if (!slug) return null;
  return LANDING_DEFINITIONS.find((d) => d.slug === slug) ?? null;
}

export function landingsByKind(): Record<LandingKind, LandingDefinition[]> {
  const out: Record<LandingKind, LandingDefinition[]> = {
    game: [], grade: [], character: [], language: [], format: [],
  };
  for (const d of LANDING_DEFINITIONS) out[d.kind].push(d);
  return out;
}

export function relatedLandings(def: LandingDefinition): LandingDefinition[] {
  return def.relatedSlugs
    .map((s) => findLanding(s))
    .filter((d): d is LandingDefinition => !!d);
}

/** Matches an auction against a landing definition's filters (OR across keyword sets, AND across filter groups when present). */
export function matchesLanding(a: AuctionCardData, def: LandingDefinition): boolean {
  const f = def.filters;
  const title = (a.title ?? "").toLowerCase();

  if (f.gradingCompany?.length) {
    const gc = (a.grading_company ?? a.custom_grading_company ?? "").toLowerCase();
    if (!f.gradingCompany.some((v) => gc.includes(v))) return false;
  }
  if (f.grade?.length) {
    const grade = (a.grade ?? a.custom_grade ?? "").toLowerCase();
    if (!f.grade.some((v) => grade.includes(v))) return false;
  }
  if (f.cardLanguage?.length) {
    const lang = (a.card_language ?? "").toLowerCase();
    if (!f.cardLanguage.some((v) => lang.includes(v))) return false;
  }
  if (f.productType?.length) {
    const pt = (a.product_type ?? "trading_card").toLowerCase();
    if (!f.productType.some((v) => pt.includes(v))) return false;
  }
  if (f.titleKeywords?.length) {
    if (!f.titleKeywords.some((v) => title.includes(v))) return false;
  }
  return true;
}
